import { useState, useEffect, useCallback } from "react";
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay } from "date-fns";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLOT_H = 48;
const START_HOUR = 8;
const END_HOUR = 19;
const HALF_HOURS = [];
for (let h = START_HOUR; h < END_HOUR; h++) {
  HALF_HOURS.push(`${String(h).padStart(2, "0")}:00`);
  HALF_HOURS.push(`${String(h).padStart(2, "0")}:30`);
}

function DraggableApt({ apt, isDragActive }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `apt-${apt.id}`, data: apt });
  const sh = parseInt(apt.start_time.split(":")[0]);
  const sm = parseInt(apt.start_time.split(":")[1]);
  const top = ((sh - START_HOUR) * 60 + sm) / 30 * SLOT_H;
  const height = Math.max(apt.duration / 30 * SLOT_H, SLOT_H);

  return (
    <div ref={setNodeRef}
      style={{ position: "absolute", top, height, left: 2, right: 2, zIndex: isDragging ? 50 : 2, opacity: isDragging ? 0.3 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, backgroundColor: `${apt.service_color || "#6366f1"}18`, borderLeft: `3px solid ${apt.service_color || "#6366f1"}` }}
      className="rounded-md px-2 py-1 cursor-grab active:cursor-grabbing transition-opacity"
      {...listeners} {...attributes} data-testid={`cal-apt-${apt.id}`}>
      <div className="text-xs font-medium text-white truncate">{apt.customer_name}</div>
      <div className="text-[10px] text-slate-400 truncate">{apt.service_name}</div>
      <div className="text-[10px] font-mono text-slate-500">{apt.start_time}-{apt.end_time}</div>
    </div>
  );
}

function SlotDrop({ id, isHour }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <div ref={setNodeRef} style={{ height: SLOT_H }}
      className={`border-b transition-colors ${isHour ? "border-slate-800/60" : "border-slate-800/20"} ${isOver ? "bg-indigo-500/15" : ""}`} />
  );
}

export default function CalendarPage() {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [appointments, setAppointments] = useState([]);
  const [activeDrag, setActiveDrag] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const load = useCallback(async () => {
    try {
      const from = format(weekDays[0], "yyyy-MM-dd");
      const to = format(weekDays[6], "yyyy-MM-dd");
      const res = await api.get("/appointments", { params: { date_from: from, date_to: to } });
      setAppointments(res.data.filter((a) => a.status !== "cancelled"));
    } catch { toast.error(t("calendar_load_error")); }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleDragStart = ({ active }) => {
    const apt = appointments.find((a) => `apt-${a.id}` === active.id);
    setActiveDrag(apt || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveDrag(null);
    if (!over) return;
    const parts = over.id.split("|");
    if (parts.length !== 2) return;
    const [newDate, newTime] = parts;
    const aptId = active.id.replace("apt-", "");
    try {
      await api.put(`/appointments/${aptId}`, { date: newDate, start_time: newTime });
      toast.success(t("calendar_reschedule_ok"));
      load();
    } catch { toast.error(t("calendar_reschedule_err")); }
  };

  return (
    <div data-testid="calendar-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight font-heading text-white">{t("calendar_title")}</h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setWeekStart((s) => subWeeks(s, 1))} data-testid="cal-prev-week"><ChevronLeft size={18} /></Button>
          <span className="text-sm text-slate-300 min-w-[200px] text-center font-medium">
            {format(weekDays[0], "MMM d")} – {format(weekDays[6], "MMM d, yyyy")}
          </span>
          <Button variant="ghost" size="icon" onClick={() => setWeekStart((s) => addWeeks(s, 1))} data-testid="cal-next-week"><ChevronRight size={18} /></Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            className="ml-2 border-slate-700 text-slate-300" data-testid="cal-today-btn">{t("calendar_today")}</Button>
        </div>
      </div>

      <div className="bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
            {/* Header */}
            <div className="grid sticky top-0 z-10 bg-slate-900 border-b border-slate-800" style={{ gridTemplateColumns: "60px repeat(7, 1fr)" }}>
              <div className="p-3" />
              {weekDays.map((d) => (
                <div key={d.toISOString()} className={`p-3 text-center border-l border-slate-800 ${isSameDay(d, new Date()) ? "bg-indigo-500/5" : ""}`}>
                  <div className="text-xs text-slate-500 uppercase">{format(d, "EEE")}</div>
                  <div className={`text-lg font-bold font-heading ${isSameDay(d, new Date()) ? "text-indigo-400" : "text-white"}`}>{format(d, "d")}</div>
                </div>
              ))}
            </div>
            {/* Grid */}
            <div className="grid" style={{ gridTemplateColumns: "60px repeat(7, 1fr)" }}>
              {/* Time labels */}
              <div>
                {HALF_HOURS.map((t) => (
                  <div key={t} style={{ height: SLOT_H }} className="flex items-start justify-end pr-2">
                    {t.endsWith(":00") && <span className="text-[10px] font-mono text-slate-500 -mt-2">{t}</span>}
                  </div>
                ))}
              </div>
              {/* Day columns */}
              {weekDays.map((d) => {
                const ds = format(d, "yyyy-MM-dd");
                const dayApts = appointments.filter((a) => a.date === ds);
                return (
                  <div key={ds} className="relative border-l border-slate-800" style={{ minHeight: HALF_HOURS.length * SLOT_H }}>
                    {HALF_HOURS.map((t) => <SlotDrop key={`${ds}|${t}`} id={`${ds}|${t}`} isHour={t.endsWith(":00")} />)}
                    {dayApts.map((a) => <DraggableApt key={a.id} apt={a} />)}
                  </div>
                );
              })}
            </div>
          </div>
          <DragOverlay>
            {activeDrag && (
              <div className="rounded-lg px-3 py-2 text-xs shadow-xl border border-indigo-400/50 bg-indigo-600/90 backdrop-blur-sm" style={{ width: 140 }}>
                <div className="font-medium text-white">{activeDrag.customer_name}</div>
                <div className="text-white/70">{activeDrag.service_name}</div>
                <div className="text-white/50 font-mono">{activeDrag.start_time}</div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
